<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import smsHttpService, { type SmsEventRow } from '@/services/http/smsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()
const route = useRoute()

// params типизированы как объединение всех роутов; для динамического [id] берём id явно.
const smsId = computed(() => String((route.params as Record<string, string>).id))

// Поллинг ~5с: отчёты о доставке приходят от провайдера асинхронно, таймлайн обновляется живьём.
const messageQuery = useQuery({
  queryKey: computed(() => ['admin', 'sms', 'detail', smsId.value]),
  queryFn: () => smsHttpService.get(smsId.value),
  refetchInterval: 5000,
})

const message = computed(() => messageQuery.data.value ?? null)
const events = computed<SmsEventRow[]>(() => message.value?.events ?? [])

// Раскрытие сырых параметров события по id — для диагностики.
const expanded = ref<Record<string, boolean>>({})
function toggleRaw(id: string) {
  expanded.value[id] = !expanded.value[id]
}

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

// Pretty-print JSON для читаемой диагностики; битый JSON показываем как есть.
function prettyJson(value: string | null): string {
  if (!value) return '—'
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

type TagSeverity = 'success' | 'warn' | 'danger' | 'secondary' | 'info'

function eventSeverity(event: SmsEventRow): TagSeverity {
  if (event.sendStatusCode === 'Failed') return 'danger'
  if (event.sendStatusCode === 'Sent') return 'success'
  if (event.deliveryStatusCode === 'Delivered') return 'success'
  if (
    event.deliveryStatusCode === 'Undelivered' ||
    event.deliveryStatusCode === 'Rejected' ||
    event.deliveryStatusCode === 'Expired'
  )
    return 'danger'
  return 'secondary'
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex flex-col gap-1">
      <RouterLink to="/sms" class="text-sm text-primary"> ← {{ t('sms.detail.back') }} </RouterLink>
      <h1 class="text-xl font-bold">{{ t('sms.detail.title') }}</h1>
    </div>

    <!-- Карточка сообщения -->
    <Card v-if="message">
      <template #content>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.type') }}</span>
            <span class="text-sm">
              {{ message.messageTypeName ?? message.messageTypeCode
              }}{{ message.invitationTypeName ? ` · ${message.invitationTypeName}` : '' }}
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.phone') }}</span>
            <span class="text-sm">{{ message.phone }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.locale') }}</span>
            <span class="text-sm">{{ message.localeCode ?? '—' }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.mobizonId') }}</span>
            <span class="text-sm">{{ message.providerMessageId ?? '—' }}</span>
          </div>
          <div v-if="message.batchId" class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.batch') }}</span>
            <RouterLink :to="`/invitations/${message.batchId}`" class="text-sm text-primary">
              {{ t('sms.detail.fields.openBatch') }}
            </RouterLink>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-surface-500">{{ t('sms.detail.fields.createdAt') }}</span>
            <span class="text-sm">{{ formatDateTime(message.createdAt) }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Таймлайн событий -->
    <Card>
      <template #title>{{ t('sms.detail.timeline.title') }}</template>
      <template #content>
        <div v-if="events.length === 0" class="text-sm text-surface-500 py-2">
          {{ t('sms.detail.timeline.empty') }}
        </div>
        <Timeline v-else :value="events" data-key="id">
          <template #content="{ item }">
            <div class="flex flex-col gap-1 pb-4">
              <div class="flex flex-wrap items-center gap-2">
                <Tag
                  :value="item.eventTypeName ?? item.eventTypeCode"
                  :severity="eventSeverity(item)"
                />
                <span class="text-xs text-surface-500">{{ formatDateTime(item.occurredAt) }}</span>
              </div>
              <div class="flex flex-wrap gap-2 text-xs text-surface-500">
                <span v-if="item.sendStatusCode">
                  {{ t('sms.detail.timeline.send') }}: {{ item.sendStatusCode }}
                </span>
                <span v-if="item.deliveryStatusCode">
                  {{ t('sms.detail.timeline.delivery') }}: {{ item.deliveryStatusCode }}
                </span>
                <span v-if="item.rawStatusCode">
                  {{ t('sms.detail.timeline.raw') }}: {{ item.rawStatusCode }}
                </span>
              </div>
              <button
                v-if="item.rawParamsJson || item.normalizedParamsJson"
                class="text-xs text-primary text-left bg-transparent border-0 p-0 cursor-pointer"
                @click="toggleRaw(item.id)"
              >
                {{
                  expanded[item.id]
                    ? t('sms.detail.timeline.hideRaw')
                    : t('sms.detail.timeline.showRaw')
                }}
              </button>
              <div v-if="expanded[item.id]" class="flex flex-col gap-2">
                <div v-if="item.normalizedParamsJson" class="flex flex-col gap-1">
                  <span class="text-xs text-surface-500">{{
                    t('sms.detail.timeline.normalizedParams')
                  }}</span>
                  <pre
                    class="text-xs whitespace-pre-wrap rounded border border-surface-200 dark:border-surface-700 p-2 overflow-auto"
                    >{{ prettyJson(item.normalizedParamsJson) }}</pre
                  >
                </div>
                <div v-if="item.rawParamsJson" class="flex flex-col gap-1">
                  <span class="text-xs text-surface-500">{{
                    t('sms.detail.timeline.rawParams')
                  }}</span>
                  <pre
                    class="text-xs whitespace-pre-wrap rounded border border-surface-200 dark:border-surface-700 p-2 overflow-auto"
                    >{{ prettyJson(item.rawParamsJson) }}</pre
                  >
                </div>
              </div>
            </div>
          </template>
        </Timeline>
      </template>
    </Card>
  </section>
</template>
